<script setup lang="ts">
  // Idioma
  const { locale, t } = useI18n()

  // Meta del SEO
  definePageMeta({
    layout: 'simple',
    showLanguageSelector: false
  })

  useSeoMeta({
    title: t('confirm.meta.title'),
    description: t('confirm.meta.description')
  })

  // Función para generar URL de imagen
  const generateImgUrl = (imagePath: string): string => {
    if (!imagePath) return ''
    if (imagePath.startsWith('http')) return imagePath
    // Asume que es una ruta relativa que necesita el dominio base
    return imagePath
  }

  // PostHog funnel
  const { trackCheckoutCompleted } = usePosthogFunnel()

  const { proxy: gtmProxy } = useScriptGoogleTagManager()

  function roundMoney(n: number): number {
    return Math.round(n * 100) / 100
  }

  /**
   * Evento GA4 `purchase` vía dataLayer (GTM).
   * Requisitos conversión / ROI (Google): transaction_id, value, currency, items.
   * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?hl=es&client_type=gtm
   * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?hl=es#purchase
   */
  function pushGa4Purchase(raw: ConfirmBookingData) {
    if (!import.meta.client) return
    const refId = raw.reference ?? raw.booking_code
    if (!refId) return

    const dedupeKey = `gtm_ga4_purchase_${refId}`
    if (sessionStorage.getItem(dedupeKey)) return

    const priceData = raw.priceData ?? {}
    const valueRaw = priceData.total
    const value =
      typeof valueRaw === 'number' && Number.isFinite(valueRaw) ? roundMoney(valueRaw) : null

    if (value === null || value <= 0) {
      if (import.meta.dev) {
        console.warn(
          '[GTM/GA4] purchase omitido: `value` no numérico o ≤ 0 (se requiere para ingresos en GA4).',
          { refId, priceData }
        )
      }
      return
    }

    const currencyRaw = String(raw.currency ?? 'EUR').toUpperCase()
    const currency = currencyRaw.length === 3 ? currencyRaw : 'EUR'

    const hotel = raw.hotelData ?? {}
    const room = raw.roomData ?? {}
    const numRooms = Math.max(1, Number(raw.numRooms) || 1)

    const roomItemId = room?.id ?? hotel?.id ?? refId
    const roomName = room?.info?.name || hotel?.name || 'Booking'

    const itemPrice = numRooms > 0 ? roundMoney(value / numRooms) : value

    const item: Record<string, unknown> = {
      item_id: String(roomItemId),
      item_name: String(roomName),
      affiliation: 'BYHOURS',
      google_business_vertical: 'hotels',
      index: 0,
      item_category: 'hotel_room',
      price: itemPrice,
      quantity: numRooms
    }
    if (hotel?.city) item.item_category2 = String(hotel.city)
    if (hotel?.chain) item.item_brand = String(hotel.chain)

    const coupon =
      raw.promoData?.applied && raw.promoData?.code ? String(raw.promoData.code) : undefined

    const ecommerce: Record<string, unknown> = {
      transaction_id: String(refId),
      value,
      currency,
      items: [item]
    }
    if (coupon) ecommerce.coupon = coupon

    const purchasePayload = {
      event: 'purchase',
      ecommerce
    }

    gtmProxy.dataLayer.push({ ecommerce: null })
    gtmProxy.dataLayer.push(purchasePayload)

    if (import.meta.dev) {
      console.info('[GTM/GA4] purchase → dataLayer', purchasePayload)
    }

    sessionStorage.setItem(dedupeKey, '1')
  }

  // Obtener datos de la reserva desde sessionStorage
  const bookingData = ref<ConfirmBookingData | null>(null)

  // Función para cargar datos
  const loadBookingData = () => {
    // Intentar cargar desde sessionStorage primero
    try {
      const sessionData = sessionStorage.getItem('byhours_booking_data')
      if (sessionData) {
        const parsedData: unknown = JSON.parse(sessionData)
        // Validar que los datos no sean muy antiguos (máximo 1 hora)
        const maxAge = 60 * 60 * 1000 // 1 hora en millisegundos
        if (
          isConfirmBookingData(parsedData)
          && (Date.now() - parsedData.timestamp) < maxAge
        ) {
          bookingData.value = parsedData
          return
        } else {
          // Datos antiguos, limpiar sessionStorage
          sessionStorage.removeItem('byhours_booking_data')
        }
      }
    } catch (error: unknown) {
      console.error('Error loading from sessionStorage:', getErrorMessage(error))
      // Limpiar sessionStorage corrupto
      if (import.meta.client) {
        sessionStorage.removeItem('byhours_booking_data')
      }
    }
  }

  // Estados
  const isLoading = ref(false) // Ya no necesitamos loading ya que usamos datos locales

  // Validar que tenemos los datos necesarios
  onMounted(() => {
    
    // Cargar datos inicialmente
    loadBookingData()

    if (!bookingData.value) {
      navigateTo(`/${locale.value}`)
    } else {
      void refreshNuxtData(['booking-widget', 'active-bookings', 'past-bookings'])

      const hotel = bookingData.value.hotelData || {}
      const room = bookingData.value.roomData || {}
      const prices = bookingData.value.priceData || {}
      const totalFromPrices = prices.total
      const totalInUserCurrency =
        typeof totalFromPrices === 'number' && Number.isFinite(totalFromPrices)
          ? totalFromPrices
          : 0
      const specificTaxes = resolveSpecificTaxes({
        amount: prices.specificTaxesNum,
        listLength: prices.specificTaxesList?.length
      })
      trackCheckoutCompleted({
        hotel_id: hotel.id,
        hotel_name: hotel.name,
        room_id: room.id,
        total_amount: amountToEur(totalInUserCurrency, bookingData.value.eur_exchange),
        currency: 'EUR',
        date: bookingData.value.date,
        pack_hours: bookingData.value.packhour,
        reference: bookingData.value.reference,
        has_specific_taxes: specificTaxes.has_specific_taxes,
        specific_taxes_amount: amountToEur(specificTaxes.specific_taxes_amount, bookingData.value.eur_exchange)
      })
      pushGa4Purchase(bookingData.value)
    }
  })

  // Limpiar sessionStorage cuando el usuario abandone la página
  onBeforeUnmount(() => {
    // Limpiar sessionStorage al salir de la página
    sessionStorage.removeItem('byhours_booking_data')
  })

  // Información de la reserva
  const reservationInfo = computed(() => {
    if (bookingData.value) {
      const hotel = bookingData.value.hotelData || {}
      const room = bookingData.value.roomData || {}
      const prices = bookingData.value.priceData || {}
      
      // Calcular hora de salida
      const calculateCheckOut = (checkIn: string, hours: number): string => {
        if (!checkIn) return ''
        const formatted = checkIn.replace(/(\d{2})(\d{2})(\d{2})/, '$1:$2')
        const [h, m] = formatted.split(':').map(Number)
        const checkInMinutes = (h || 0) * 60 + (m || 0)
        const checkOutMinutes = checkInMinutes + (hours * 60)
        const checkOutHours = Math.floor(checkOutMinutes / 60) % 24
        const checkOutMins = checkOutMinutes % 60
        return `${checkOutHours.toString().padStart(2, '0')}:${checkOutMins.toString().padStart(2, '0')}`
      }

      return {
        // Datos del hotel (desde hotelData del checkout)
        hotelName: hotel.name || 'Hotel',
        hotelStars: splitEstrellas(hotel.stars?.abbr ?? ''),
        hotelAddress: hotel.address || '',
        hotelCity: hotel.city || '',
        hotelImage: hotel.images?.[0] || '',
        hotelLatitude: parseFloat(hotel.latitude ?? '') || null,
        hotelLongitude: parseFloat(hotel.longitude ?? '') || null,
        
        // Datos de la habitación y reserva
        roomType: room.info?.name || 'Habitación',
        date: bookingData.value.date,
        duration: `${bookingData.value.packhour}h`,
        checkIn: bookingData.value.hour?.replace(/(\d{2})(\d{2})(\d{2})/, '$1:$2'),
        checkOut: calculateCheckOut(bookingData.value.hour || '', bookingData.value.packhour || 0),
        numRooms: bookingData.value.numRooms || 1,
        
        // Datos de pago (desde bookingData)
        totalAmount: bookingData.value.totalAmount,
        currency: bookingData.value.currency,
        
        // Información de reserva
        reference: bookingData.value.reference,
        confirmed: bookingData.value.confirmed,
        bookingCode: bookingData.value.booking_code,
        
        // Datos personales (ahora disponibles desde el formulario)
        holderName: bookingData.value.holderFirstName || '',
        holderSurname: bookingData.value.holderLastName || '',
        holderEmail: bookingData.value.holderEmail || '',
        holderPhone: bookingData.value.holderPhone || '',
        comments: bookingData.value.comments || '',
        
        // Precios desglosados (ahora disponibles desde priceData)
        priceRoom: prices.priceRoom || '',
        bookingFee: prices.fee || '',
        toPayAtHotel: prices.destination || '',
        toPayNow: prices.now || '',
        totalFormatted: prices.total || '',
        specificTaxes: prices.specificTaxes || '',
        hasSpecificTaxes: (prices.specificTaxesNum || 0) > 0,
        destination: prices.destination || '',
        
        // Datos promocionales si se aplicaron
        hasPromo: bookingData.value.promoData?.applied || false,
        promoDiscount: bookingData.value.promoData?.discountFormatted || '',
        promoCode: bookingData.value.promoData?.code || '',
        
        // Políticas (información genérica por ahora)
        cancellationPolicy: '',
        paymentPolicy: '',
        noShowPolicy: ''
      }
    }
    return null
  })

</script>

<template>
  <div class="min-h-screen">

    <div class="max-w-screen-sm mx-auto px-4 py-10">
      
      <!-- Loading state -->
      <div v-if="isLoading" class="text-center py-16">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
        <p class="text-gray-600">{{ t('confirm.loading') }}</p>
      </div>

      <!-- Success state -->
      <div v-else>
        
          <!-- Success header -->
          <div class="text-center mb-8 md:mb-10">
            <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3 md:mb-4">
              <UIcon name="i-lucide-check" class="h-6 w-6 text-green-600" />
            </div>
            <h1 class="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {{ t('confirm.header.title') }}
            </h1>
            <p class="text-base text-gray-600">
              {{ t('confirm.header.subtitle') }}
            </p>
            <div v-if="bookingData" class="mt-4 flex justify-center">
              <div class="items-center w-fit border border-gray-200 bg-gray-50 shadow-xs p-2 px-3 md:px-4 rounded-xl">
                <span class="text-sm mr-1">{{ t('confirm.header.reference') }} </span>
                <span class="text-base font-bold">{{ bookingData?.reference }}</span>
              </div>
            </div>
          </div>

        <!-- Main content grid -->
        <div v-if="bookingData" class="grid grid-cols-1 lg:grid-cols-2 gap-8">

          <!-- Hotel info and map -->
          <div class="space-y-6 lg:col-span-2">
            
            <!-- Hotel card - replicando diseño del checkout -->
            <div class="bg-white rounded-3xl p-5 h-fit shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]">
              
              <!-- Hotel card -->
              <div class="flex gap-3 md:gap-4 mb-6">
                <div class="w-24 h-20 md:w-44 md:h-28 bg-gray-200 rounded-lg md:rounded-xl overflow-hidden flex-shrink-0">
                  <NuxtImg 
                    v-if="bookingData?.hotelData?.images?.[0]"
                    provider="byhours"
                    format="webp"
                    :src="generateImgUrl(bookingData.hotelData.images[0])"
                    alt="Hotel" 
                    class="w-full h-full object-cover"
                    width="176"
                    height="112"
                  />
                  <div v-else class="w-full h-full flex items-center justify-center">
                    <UIcon name="i-lucide-building-2" class="h-6 w-6 md:h-8 md:w-8 text-gray-400" />
                  </div>
                </div>
                <div class="flex flex-col">
                  <div>
                    <span v-if="!splitEstrellas(bookingData?.hotelData?.stars?.abbr).countEstrellas" class="text-neutral-500 text-sm">-</span>
                    <UIcon v-for="index in splitEstrellas(bookingData?.hotelData?.stars?.abbr).countEstrellas" :key="index" name="i-heroicons-star-solid" class="text-sm"/>
                    <span class="text-sm">{{ splitEstrellas(bookingData?.hotelData?.stars?.abbr).rightString }}</span>
                  </div>
                  <p class="font-bold text-base md:text-lg">
                    {{ bookingData?.hotelData?.name || t('checkout.summary.hotel') }}
                  </p>
                  <p class="text-neutral-500 text-sm mt-1">
                    {{ bookingData?.hotelData?.address || t('checkout.summary.address') }}
                  </p>
                </div>
              </div>

              <!-- Fechas -->
              <CheckDates
                :checkinHour="bookingData?.hour || ''"
                :packHour="bookingData?.packhour || 0"
                :checkinDate="bookingData?.date || ''"
                :extraHours="bookingData?.extraHours || 0"
              />

              <USeparator />

              <div class="flex flex-col gap-6 py-4">
                
                <!-- Detalles de la habitación -->
                <div class="flex flex-col gap-2">
                  
                  <!-- Precio de la habitación -->
                  <div class="flex justify-between items-start gap-3">
                    <span class="text-sm">{{ bookingData?.roomData?.info?.name }}<span v-if="(bookingData?.numRooms ?? 0) > 1" class="text-gray-500"> x{{ bookingData?.numRooms ?? 0 }}</span></span>
                    <span class="text-sm">{{ bookingData?.priceData?.priceRoom || '' }}</span>
                  </div>
                  
                  <!-- Tasas e impuestos del hotel -->
                  <div v-if="(bookingData?.priceData?.specificTaxesList?.length ?? 0) > 0" class="flex flex-col gap-2">
                    <div v-for="(tax, index) in (bookingData?.priceData?.specificTaxesList ?? [])" :key="index" class="flex justify-between items-center gap-3">
                      <div class="flex items-center gap-2 justify-between w-full">
                        <span class="text-sm">{{ tax?.label || '' }}</span>
                        <span class="text-sm">{{ tax?.amountFormat || '' }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div v-else-if="(bookingData?.priceData?.specificTaxesNum ?? 0) > 0" class="flex justify-between items-center gap-3">
                    <div class="flex items-center gap-2">
                      <span class="text-sm">{{ t('checkout.summary.hotelTaxes') }}</span>
                    </div>
                    <span class="text-sm">{{ bookingData?.priceData?.specificTaxes || '' }}</span>
                  </div>

                  <!-- Extras seleccionados -->
                  <template v-if="(bookingData?.extrasData?.length ?? 0) > 0">
                    <div v-for="extra in (bookingData?.extrasData ?? [])" :key="extra.id" class="flex justify-between items-center gap-3">
                      <div class="flex items-center gap-2">
                        <span class="text-sm">{{ extra.name }}</span>
                        <span v-if="(extra.quantity ?? 0) > 1" class="text-sm text-gray-500">x{{ extra.quantity ?? 0 }}</span>
                      </div>
                      <span class="text-sm">{{ extra.price }}</span>
                    </div>
                  </template>

                  <!-- Descuento promocional (si está aplicado) -->
                  <div v-if="bookingData?.promoData?.applied" class="flex justify-between items-center text-success">
                    <span class="text-sm">{{ t('checkout.summary.promoDiscount') }} ({{ bookingData?.promoData?.code }})</span>
                    <span class="text-sm font-medium">-{{ bookingData?.promoData?.discountFormatted }}</span>
                  </div>

                  <!-- Tasas de reserva -->
                  <div v-if="bookingData?.priceData?.fee" class="flex justify-between items-center">
                    <div class="flex items-center gap-2">
                      <span class="text-sm">{{ t('checkout.summary.bookingFees') }}</span>
                    </div>
                    <span class="text-sm">{{ bookingData?.priceData?.fee || '' }}</span>
                  </div>
                </div>

                <USeparator />

                <!-- Desglose de pagos -->
                <div v-if="bookingData?.priceData?.destination" class="flex flex-col gap-3">
                  <div class="flex justify-between items-center">
                    <div class="flex items-center gap-2">
                      <span>{{ t('checkout.summary.payAtHotel') }}</span>
                    </div>
                    <span>{{ bookingData?.priceData?.destinationFormat }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span>{{ t('checkout.summary.payNow') }}</span>
                    <span>{{ bookingData?.priceData?.now }}</span>
                  </div>
                </div>
              </div>

              <!-- Total -->
              <div>
                <div class="flex justify-between items-center">
                  <span class="text-lg font-bold">{{ t('checkout.summary.total') }}</span>
                  <span class="text-lg font-bold">{{ bookingData?.priceData?.totalFormat || '' }}</span>
                </div>
              </div>

            </div>

          </div>

          <!-- Personal data -->
          <div v-if="bookingData && (bookingData.holderFirstName || bookingData.holderEmail || bookingData.holderPhone)" class="bg-white rounded-3xl p-5 shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]">
            <h2 class="text-xl text-gray-900 mb-4">{{ t('confirm.personalData.title') }}</h2>
            <div class="space-y-3 text-sm">
              <div v-if="bookingData.holderFirstName">
                <span class="text-gray-600 block">{{ t('confirm.personalData.name') }}</span>
                <span class="font-medium text-gray-900">{{ bookingData.holderFirstName }}</span>
              </div>
              <div v-if="bookingData.holderLastName">
                <span class="text-gray-600 block">{{ t('confirm.personalData.lastName') }}</span>
                <span class="font-medium text-gray-900">{{ bookingData.holderLastName }}</span>
              </div>
              <div v-if="bookingData.holderEmail">
                <span class="text-gray-600 block">{{ t('confirm.personalData.email') }}</span>
                <span class="font-medium text-gray-900">{{ bookingData.holderEmail }}</span>
              </div>
              <div v-if="bookingData.holderPhone">
                <span class="text-gray-600 block">{{ t('confirm.personalData.phone') }}</span>
                <span class="font-medium text-gray-900">{{ bookingData.holderPhone }}</span>
              </div>
            </div>
          </div>

          <!-- Map -->
          <div v-if="bookingData?.hotelData?.latitude && bookingData?.hotelData?.longitude" class="bg-white rounded-3xl p-5 shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]">
            <h2 class="text-xl text-gray-900 mb-4">{{ t('confirm.location.title') }}</h2>
            <div class="h-52 rounded-lg overflow-hidden">
              <ClientOnly>
                <LMap
                  class="rounded-2xl z-0"
                  ref="map"
                  :zoom="13"
                  :center="[parseFloat(bookingData.hotelData.latitude), parseFloat(bookingData.hotelData.longitude)]"
                  :use-global-leaflet="false"
                  :options="{ zoomControl: true, attributionControl: false, scrollWheelZoom: false }"
                >
                  <LTileLayer
                    :url="`http://{s}.google.com/vt/lyrs=m&hl=${locale}&x={x}&y={y}&z={z}`"
                    :minZoom="3"
                    :maxZoom="18"
                    :subdomains="['mt0', 'mt1', 'mt2', 'mt3']"
                  />
                  <LMarker :lat-lng="[parseFloat(bookingData.hotelData.latitude), parseFloat(bookingData.hotelData.longitude)]">
                    <LIcon>
                      <UIcon name="i-heroicons-map-pin-solid" class="text-neutral-900 -ml-3 -mt-3 text-4xl" />
                    </LIcon>
                  </LMarker>
                </LMap>
                <template #fallback>
                  <USkeleton class="w-full h-36 md:h-56 rounded-2xl" />
                </template>
              </ClientOnly>
            </div>
          </div>

        </div>

        <div v-else>
          <USkeleton class="w-full h-96 rounded-3xl" />
        </div>

        <!-- Detalles de pago y cancelación -->
        <div class="mt-8 grid grid-cols-1 gap-6">
          
          <!-- Información de pago y políticas -->
          <div class="bg-white rounded-3xl p-5 shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]">
            <h2 class="text-xl mb-4">{{ t('checkout.paymentDetails.title') }}</h2>
            
            <!-- Destacados de políticas -->
            <div class="text-sm w-full flex flex-col gap-3 mb-6">
              <div class="flex items-center">
                <UIcon name="i-lucide-check-circle-2" class="w-3 h-3 mr-2" />
                <span>{{ t('checkout.summary.freeCancellation') }}</span>
              </div>
              <div class="flex items-center">
                <UIcon name="i-lucide-check-circle-2" class="w-3 h-3 mr-2" />
                <span>{{ t('checkout.summary.payAtHotelTooltip') }}</span>
              </div>
            </div>
            
            <div class="space-y-4">
              <div class="flex flex-col gap-2">
                <p class="font-semibold text-sm">{{ t('checkout.paymentDetails.paymentCharges.title') }}</p>
                <ul class="list-disc list-inside text-sm gap-2 flex flex-col text-gray-600">
                  <li>{{ t('checkout.paymentDetails.paymentCharges.item1') }}</li>
                  <li v-if="!reservationInfo?.destination">{{ t('checkout.paymentDetails.paymentCharges.item3') }}</li>
                  <li v-else>{{ t('checkout.paymentDetails.paymentCharges.item2') }}</li>
                </ul>
              </div>
              
              <div class="flex flex-col gap-2">
                <p class="font-semibold text-sm">{{ t('checkout.paymentDetails.cancellations.title') }}</p>
                <ul class="list-disc list-inside text-sm gap-2 flex flex-col text-gray-600">
                  <li>{{ t('checkout.paymentDetails.cancellations.item1') }}</li>
                  <li>{{ t('checkout.paymentDetails.cancellations.item2') }}</li>
                  <li>{{ t('checkout.paymentDetails.cancellations.item3') }}</li>
                </ul>
              </div>

              <div class="flex flex-col gap-2">
                <p class="font-semibold text-sm">{{ t('checkout.paymentDetails.checkIn.title') }}</p>
                <ul class="list-disc list-inside text-sm gap-2 flex flex-col text-gray-600">
                  <li>{{ t('checkout.paymentDetails.checkIn.idRequired') }}</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Atención al cliente -->
          <div class="flex flex-col gap-2 bg-white rounded-3xl p-5 shadow-[0px_0px_10px_0_rgba(0,0,0,0.1)]">
            <h2 class="text-xl flex items-center">
              {{ t('confirm.help.title') }}
            </h2>
            <div class="text-sm">
              <p class="mt-1">{{ t('checkout.paymentDetails.support.scheduleMondayThursday') }}</p>
              <p>{{ t('checkout.paymentDetails.support.scheduleFriday') }}</p>
              <p>{{ t('checkout.paymentDetails.support.scheduleWeekend') }}</p>
            </div>
            <p class="text-sm mt-1">
              {{ t('checkout.paymentDetails.support.whatsapp') }} <a href="https://wa.me/34617479117" target="_blank" class="text-orange-500 hover:text-orange-600">Whatsapp</a>
            </p>
            <p class="text-sm mt-1">
              {{ t('checkout.paymentDetails.support.email') }} <a href="mailto:hello@byhours.com" class="text-orange-500 hover:text-orange-600">hello@byhours.com</a>
            </p>
            <ul class="text-sm gap-2 flex flex-col mt-2">
              <li>🇪🇸 {{ t('checkout.paymentDetails.support.spain') }} <span class="text-neutral-500">+34 602 225 694</span></li>
              <li>🇨🇴 {{ t('checkout.paymentDetails.support.colombia') }} <span class="text-neutral-500">+57 1 4898 378</span></li>
              <li>🇲🇽 {{ t('checkout.paymentDetails.support.mexico') }} <span class="text-neutral-500">+52 55 8526 4283</span></li>
              <li>🇫🇷 {{ t('checkout.paymentDetails.support.france') }} <span class="text-neutral-500">+33 1 7890 2675</span></li>
              <li>🇬🇧 {{ t('checkout.paymentDetails.support.uk') }} <span class="text-neutral-500">+44 20 3769 0610</span></li>
            </ul>
          </div>

        </div>

        <!-- Action buttons -->
        <div class="flex flex-col sm:flex-row gap-4 justify-center items-center mt-8 mb-12">
          <UButton
            :to="`/${locale}/user-zone/bookings`"
            class="bg-byhours-gradient"
            size="xl"
          >
            {{ t('confirm.buttons.viewBookings') }}
          </UButton>
          
          <UButton
            :to="`/${locale}/search`"
            color="primary"
            variant="soft"
            size="xl"
          >
            {{ t('confirm.buttons.searchMore') }}
          </UButton>
        </div>
      </div>
    </div>
  </div>
</template> 